import { __assign } from 'tslib';
import React__default, { useState, useEffect } from 'react';
import { KpkFilterModifierType, dayjs } from '@keepeek/commons';
import AdapterDayjs from '@mui/lab/AdapterDayjs';
import DatePicker from '@mui/lab/DatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { Grid, FormControl, InputLabel, Select, MenuItem, TextField } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import StyledFilterDate, { classes } from './index.styled.js';

function getAllowedOptionsValues() {
    return [
        KpkFilterModifierType.DateToday,
        KpkFilterModifierType.DateYesterday,
        KpkFilterModifierType.DateLast7Days,
        KpkFilterModifierType.DateLast30Days,
        KpkFilterModifierType.DateLastMonth,
        KpkFilterModifierType.DateCurrentMonth,
        KpkFilterModifierType.FromTo,
        KpkFilterModifierType.Empty,
        KpkFilterModifierType.NotEmpty,
    ];
}
var FilterDate = function (_a) {
    var _b, _c;
    var className = _a.className, filter = _a.filter, onFilterUpdate = _a.onFilterUpdate, expandIcon = _a.expandIcon, _d = _a.templateFormat, templateFormat = _d === void 0 ? "DD/MM/YYYY" : _d, expanded = _a.expanded, setExpanded = _a.setExpanded, sx = _a.sx;
    var t = useTranslation("refront-components").t;
    var fromDate = filter.values && filter.values.length > 0 && filter.values[0].id
        ? dayjs(filter.values[0].id)
        : null;
    var toDate = filter.values && filter.values.length > 1 && filter.values[1].id
        ? dayjs(filter.values[1].id)
        : null;
    var initialSelectedModifier = ((_c = (_b = filter === null || filter === void 0 ? void 0 : filter.modifiers) === null || _b === void 0 ? void 0 : _b.find(function (m) { return m.selected; })) === null || _c === void 0 ? void 0 : _c.type) || KpkFilterModifierType.FromTo;
    var _e = useState(initialSelectedModifier), currentOption = _e[0], setCurrentOption = _e[1];
    // Need to use useEffect here to change state if derived prop state change :
    // https://reacthustle.com/blog/how-to-update-state-when-props-change-in-react
    useEffect(function () { return setCurrentOption(initialSelectedModifier); }, [initialSelectedModifier]);
    var handleOptionChange = function (value) {
        var _a;
        if (value) {
            setCurrentOption(value);
            // do not manage "onFilterUpdate" for "FromTo" type => it will be managed by "handleDateChange"
            if (value !== KpkFilterModifierType.FromTo) {
                onFilterUpdate(__assign(__assign({}, filter), { modifiers: ((_a = filter === null || filter === void 0 ? void 0 : filter.modifiers) === null || _a === void 0 ? void 0 : _a.map(function (m) {
                        return __assign(__assign({}, m), { selected: value === m.type ? true : null });
                    })) || [], values: [] }));
            }
        }
    };
    function handleDateChange(date, type) {
        var _a;
        var values;
        if (type === "from") {
            values = [
                {
                    id: date,
                    label: date ? date.format(templateFormat) : "",
                    selected: null
                },
                {
                    id: toDate,
                    label: toDate ? toDate.format(templateFormat) : "",
                    selected: null
                },
            ];
        }
        else {
            values = [
                {
                    id: fromDate,
                    label: fromDate ? fromDate.format(templateFormat) : "",
                    selected: null
                },
                {
                    id: date,
                    label: date ? date.format(templateFormat) : "",
                    selected: null
                },
            ];
        }
        if (date === null || date === void 0 ? void 0 : date.isValid()) {
            onFilterUpdate(__assign(__assign({}, filter), { modifiers: ((_a = filter === null || filter === void 0 ? void 0 : filter.modifiers) === null || _a === void 0 ? void 0 : _a.map(function (m) {
                    return __assign(__assign({}, m), { selected: KpkFilterModifierType.FromTo === m.type ? true : null });
                })) || [], values: values }));
        }
    }
    return (React__default.createElement(StyledFilterDate, { className: clsx("FilterDate", className), filter: filter, expanded: expanded, setExpanded: setExpanded, expandIcon: expandIcon, testId: "FilterDate", sx: sx },
        React__default.createElement(Grid, { container: true, justifyContent: "space-around", sx: { gap: "16px" } },
            React__default.createElement(FormControl, { className: classes.selectFormControl, fullWidth: true, size: "small", variant: "outlined" },
                React__default.createElement(InputLabel, { id: "FilterDate-".concat(filter.type, "-").concat(filter.id, "-label") }, t("components.filter-date.input-select.label")),
                React__default.createElement(Select, { role: "combobox", onChange: function (_a) {
                        var target = _a.target;
                        return handleOptionChange(target.value);
                    }, id: "FilterDate-".concat(filter.type, "-").concat(filter.id), labelId: "FilterDate-".concat(filter.type, "-").concat(filter.id, "-label"), label: t("components.filter-date.input-select.label"), value: currentOption }, getAllowedOptionsValues().map(function (val) { return (React__default.createElement(MenuItem, { key: val, value: val, sx: {
                        padding: function (theme) { return theme.spacing(0, 1, 0, 1); },
                        fontSize: "0.875rem",
                        minHeight: "0px"
                    } }, t("components.filter-date.option.".concat(val, ".label")))); }))),
            currentOption === KpkFilterModifierType.FromTo && (React__default.createElement(LocalizationProvider, { dateAdapter: AdapterDayjs },
                React__default.createElement(DatePicker, { leftArrowButtonText: t("components.date-picker.previous-month"), rightArrowButtonText: t("components.date-picker.next-month"), OpenPickerButtonProps: { "aria-label": t("components.date-picker.open-calendar") }, getViewSwitchingButtonText: function (currentView) {
                        if (currentView === "year") {
                            return t("components.date-picker.view-year");
                        }
                        return t("components.date-picker.view-month");
                    }, inputFormat: templateFormat, label: t("components.filter-date.from.label"), value: fromDate, onChange: function (date) { return handleDateChange(date, "from"); }, className: classes.textfield, renderInput: function (inputProps) { return (React__default.createElement(TextField, __assign({ "aria-label": t("components.filter-date.from.aria"), fullWidth: true, id: "date-from", "data-testid": "dateFromFilter" }, inputProps, { autoComplete: "off", variant: "outlined", size: "small" }))); } }),
                React__default.createElement(DatePicker, { leftArrowButtonText: t("components.date-picker.previous-month"), rightArrowButtonText: t("components.date-picker.next-month"), OpenPickerButtonProps: { "aria-label": t("components.date-picker.open-calendar") }, getViewSwitchingButtonText: function (currentView) {
                        if (currentView === "year") {
                            return t("components.date-picker.view-year");
                        }
                        return t("components.date-picker.view-month");
                    }, inputFormat: templateFormat, label: t("components.filter-date.to.label"), value: toDate, onChange: function (date) { return handleDateChange(date, "to"); }, className: classes.textfield, renderInput: function (inputProps) { return (React__default.createElement(TextField, __assign({ "aria-label": t("components.filter-date.to.aria"), fullWidth: true, id: "date-to", "data-testid": "dateToFilter" }, inputProps, { autoComplete: "off", variant: "outlined", size: "small" }))); } }))))));
};

export { FilterDate as default, getAllowedOptionsValues };
//# sourceMappingURL=index.js.map
